import { Link } from "react-router-dom";
import { ArrowRight, Award, Truck, Factory, ShieldCheck, Star } from "lucide-react";
import { BRAND, CATEGORIES, HOTEL_BRANDS } from "@/data/products";

const HomePage = () => {
  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section data-testid="hero-section" className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 hh-fade-in">
            <span className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-[#D4AF37]">
              <span className="w-10 h-px bg-[#D4AF37]" />
              Since 2023 · Factory Direct
            </span>
            <h1 className="font-serif-display mt-6 text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-[#1A1A1A]">
              Where luxury<br />meets <em className="text-[#D4AF37] not-italic font-medium">Affordability.</em>
            </h1>
            <p className="mt-7 text-base sm:text-lg text-[#4A4A4A] leading-relaxed max-w-xl">
              Hospitality Hub supplies bed frames, LED mirrors, PTACs, hospitality TVs, furniture and lighting to hotels across the United States, directly from the factory, without the middlemen markup.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/contact" data-testid="hero-cta-quote" className="border border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white text-[#1A1A1A] px-8 py-4 uppercase tracking-[0.2em] text-xs transition-colors">
                Request a Quote
              </Link>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
              <div>
                <p className="font-serif-display text-3xl text-[#D4AF37]">500+</p>
                <p className="text-xs uppercase tracking-wider text-[#4A4A4A] mt-1">US Hotels Served</p>
              </div>
              <div>
                <p className="font-serif-display text-3xl text-[#D4AF37]">100%</p>
                <p className="text-xs uppercase tracking-wider text-[#4A4A4A] mt-1">Factory Direct</p>
              </div>
              <div>
                <p className="font-serif-display text-3xl text-[#D4AF37]">24-Hour</p>
                <p className="text-xs uppercase tracking-wider text-[#4A4A4A] mt-1">Quote Turnaround Time</p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 relative hh-fade-in">
            <div className="relative aspect-[4/5] w-full overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1611892440504-42a792e24d32?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
                alt="Luxury hotel interior"
                className="w-full h-full object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 border border-[#D4AF37]/30 shadow-xl max-w-xs">
                <div className="flex items-center gap-1 mb-2">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />)}
                </div>
                <p className="text-sm text-[#1A1A1A] leading-snug italic font-serif-display">"Unmatched pricing without compromising on quality. Our go-to supplier for every renovation."</p>
                <p className="text-xs uppercase tracking-wider text-[#4A4A4A] mt-3">Marriott Property Owner</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUSTED BY */}
      <section data-testid="trusted-by-section" className="border-y border-[#D4AF37]/20 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-16 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Our Clients</p>
          <h2 data-testid="trusted-heading" className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#1A1A1A] leading-tight">
            Trusted Across The Hospitality Industry
          </h2>
          <p data-testid="trusted-subheading" className="mt-4 text-[#4A4A4A] max-w-2xl mx-auto">
            Supporting hotels, resorts, extended-stay properties, and hospitality groups across the United States.
          </p>
        </div>
        <div className="hh-marquee-mask pb-14">
          <div data-testid="brand-marquee" className="hh-marquee-track" style={{ gap: "70px" }}>
            {[...HOTEL_BRANDS, ...HOTEL_BRANDS].map((b, idx) => (
              <div
                key={`${idx}-${b.name}`}
                data-testid={idx < HOTEL_BRANDS.length ? `hotel-brand-${b.name.toLowerCase().replace(/\s+/g, "-")}` : undefined}
                aria-hidden={idx >= HOTEL_BRANDS.length}
                className="flex items-center justify-center h-10 shrink-0"
                title={b.name}
              >
                {b.logo ? (
                  <img
                    src={b.logo}
                    alt={b.name}
                    loading="lazy"
                    className="h-9 md:h-10 w-auto object-contain opacity-60 hover:opacity-100 transition-opacity duration-500"
                  />
                ) : (
                  <span
                    className="text-[#64748b] opacity-60 hover:opacity-100 transition-opacity duration-500 whitespace-nowrap select-none leading-none"
                    style={b.textStyle}
                  >
                    {b.display}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section data-testid="categories-section" className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Our Collection</p>
            <h2 className="font-serif-display text-4xl lg:text-5xl">Hospitality, supplied.</h2>
          </div>
          <p className="max-w-md text-[#4A4A4A]">Five complete categories covering every piece your property needs, sourced directly from the manufacturer.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {CATEGORIES.map((c, idx) => (
            <Link
              key={c.slug}
              to={`/products/${c.slug}`}
              data-testid={`home-category-${c.slug}`}
              className={`group relative overflow-hidden bg-white border border-[#E5E5E5] hover:border-[#D4AF37]/60 transition-all ${idx === 0 ? "md:col-span-2 lg:col-span-2 lg:row-span-2" : ""}`}
            >
              <div className={`${idx === 0 ? "aspect-[16/12]" : "aspect-[4/3]"} overflow-hidden`}>
                <img src={c.hero} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-6">
                <h3 className="font-serif-display text-2xl mb-2">{c.name}</h3>
                <p className="text-sm text-[#4A4A4A] line-clamp-2">{c.blurb}</p>
                <span className="inline-flex items-center gap-2 mt-4 text-xs uppercase tracking-[0.2em] text-[#D4AF37] group-hover:gap-3 transition-all">
                  Browse <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* WHY US */}
      <section data-testid="why-us-section" className="bg-white border-y border-[#D4AF37]/20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Why Hospitality Hub</p>
            <h2 className="font-serif-display text-4xl lg:text-5xl leading-tight">Factory-direct pricing, hotel-grade quality.</h2>
            <p className="mt-6 text-[#4A4A4A] leading-relaxed">
              What sets us apart is our factory-direct pricing model, allowing us to offer unmatched value to hoteliers across the US without compromising on the quality, certifications and warranties major brands require.
            </p>
            <Link to="/about" data-testid="why-us-learn-more" className="mt-8 inline-flex items-center gap-3 text-sm uppercase tracking-[0.2em] text-[#1A1A1A] hover:text-[#D4AF37] transition-colors">
              Our Story <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              { icon: Factory, title: "Factory Direct", desc: "No middlemen, no markups. Direct lines to our manufacturing partners." },
              { icon: Award, title: "Brand Approved", desc: "Products meeting Marriott, Hilton, IHG, Wyndham & Choice brand standards." },
              { icon: Truck, title: "Nationwide Logistics", desc: "Coast-to-coast shipping coordinated by our dedicated logistics team." },
              { icon: ShieldCheck, title: "UL / DLC / ETL", desc: "All electronics and lighting certified to US safety and energy standards." },
            ].map((f) => (
              <div key={f.title} className="p-7 border border-[#E5E5E5] bg-[#FAF9F6]">
                <f.icon className="w-7 h-7 text-[#D4AF37]" />
                <h4 className="font-serif-display text-xl mt-4">{f.title}</h4>
                <p className="text-sm text-[#4A4A4A] mt-2 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="cta-section" className="max-w-7xl mx-auto px-6 md:px-12 py-24 text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Start Your Project</p>
        <h2 className="font-serif-display text-4xl lg:text-6xl max-w-3xl mx-auto leading-tight">Ready to renovate or open a new property?</h2>
        <p className="mt-6 text-[#4A4A4A] max-w-xl mx-auto">Get a custom factory-direct quote within 24 hours. No obligation.</p>
        <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
          <Link to="/contact" data-testid="cta-contact-btn" className="bg-[#D4AF37] hover:bg-[#B5952F] text-white px-10 py-4 uppercase tracking-[0.2em] text-xs transition-colors">Request a Quote</Link>
          <a href={`tel:${BRAND.phone}`} data-testid="cta-phone-btn" className="border border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white text-[#1A1A1A] px-10 py-4 uppercase tracking-[0.2em] text-xs transition-colors">Call {BRAND.phone}</a>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
